import { useXNGSelector } from "../../../../../context/store";
import { selectStateInUS } from "../../../../../context/slices/stateInUsSlice";
import isInState from "../../../../students/views/student_profile/utils/is_in_state";

/**
 * The two Good Faith Effort feature flags, kept in one file so the surfaces they gate can never
 * disagree about whether the feature is on.
 *
 *   AdminGoodFaithEffortActive  — the admin tool (XLOGS-161): sidebar entry + page self-guard.
 *   GoodFaithEffortActive       — the read-only Student Profile panel (XLOGS-162).
 *
 * Split admin/provider the same way the app already splits them:
 * `TransportationAccommodationsActive` / `TransportationAccommodationsAdminActive`, and
 * `OrderedServicesTableActive` / `AdminOrderedServicesActive`. Two flags because the two surfaces have
 * different audiences and different backing policies — the admin tool reads `AccountAdminAccess`
 * endpoints, the profile panel reads an `AccountAccess` one — so they can be rolled out apart.
 *
 * The names are ours: neither flag exists in Azure App Configuration until the PR goes up, so until then
 * both selectors return `undefined` and both surfaces stay invisible. That is the expected state.
 */
export const GOOD_FAITH_EFFORT_ADMIN_FLAG_KEY = "AdminGoodFaithEffortActive";
export const GOOD_FAITH_EFFORT_PROFILE_FLAG_KEY = "GoodFaithEffortActive";

/** @deprecated Use {@link GOOD_FAITH_EFFORT_ADMIN_FLAG_KEY}. Kept so existing imports keep resolving. */
export const GOOD_FAITH_EFFORT_FLAG_KEY = GOOD_FAITH_EFFORT_ADMIN_FLAG_KEY;

/**
 * LOCAL VERIFICATION ONLY — set back to `false` before the PR.
 *
 * Neither flag exists in App Configuration yet, so there is no way to switch either surface on in a
 * local environment. Flipping this to `true` renders them both.
 *
 * This must not merge in the `true` state: it would expose both surfaces in every New Mexico environment
 * regardless of the flags, which is the whole thing the flags are there to prevent. The New Mexico gate
 * below still applies, so a `true` here cannot leak the feature into Texas or New Hampshire.
 */
const FORCE_ENABLED_FOR_LOCAL_VERIFICATION = false;

/**
 * Why the New Mexico gate is not optional, and why it is applied to the flag rather than beside it.
 *
 * The 180-day recoupment rule, the MCO list and the eligibility methods are all New Mexico's, so this
 * feature is simply wrong for any other state. The flag resolves per account through the configuration
 * endpoint (`featureFlagsSlice.ts`), so a global value or a mistaken state default would light the
 * feature up for Texas districts too.
 *
 * With the state check folded in here, the flag can only ever enable the feature for New Mexico.
 * `prescriptions_section.tsx` gates the NM-only ORP Provider title the same way.
 */
function useGoodFaithEffortEnabled(flagKey: string): boolean {
  const flag = useXNGSelector((state) => state.featureFlags.flags[flagKey]);
  const stateInUs = useXNGSelector(selectStateInUS);

  return (FORCE_ENABLED_FOR_LOCAL_VERIFICATION || !!flag) && isInState(stateInUs ?? "", "NM");
}

/** The admin tool (XLOGS-161). */
export default function useGoodFaithEffortFlag(): boolean {
  return useGoodFaithEffortEnabled(GOOD_FAITH_EFFORT_ADMIN_FLAG_KEY);
}

/** The read-only Student Profile panel (XLOGS-162). */
export function useGoodFaithEffortProfileFlag(): boolean {
  return useGoodFaithEffortEnabled(GOOD_FAITH_EFFORT_PROFILE_FLAG_KEY);
}
